import React from 'react';
import { CheckCircle, Zap, Target } from 'lucide-react';

interface ReportSectionProps {
  title: string;
  subtitle: string;
  description: string;
  keyInsight: string;
  nextSteps: string[];
  wealthSteps: string[];
  color: string;
  icon: React.ReactNode;
}

const ReportSection: React.FC<ReportSectionProps> = ({
  title,
  subtitle,
  description,
  keyInsight,
  nextSteps,
  wealthSteps,
  color,
  icon,
}) => {
  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 mb-12">
      <div className="text-center mb-8">
        <div className={`w-24 h-24 rounded-full bg-gradient-to-r ${color} flex items-center justify-center mx-auto mb-6`}>
          {icon}
        </div>
        <h2 className="text-3xl font-bold text-white mb-2">{title}</h2>
        <p className="text-xl text-blue-300 mb-4">{subtitle}</p>
        <p className="text-lg text-gray-300 max-w-2xl mx-auto">{description}</p>
      </div>

      <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-6 mb-8">
        <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
          <Zap className="w-5 h-5 mr-2 text-yellow-400" />
          Your Key Insight
        </h3>
        <p className="text-gray-200 text-lg">{keyInsight}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-xl font-semibold text-white mb-4">Your Priority W.E.A.L.T.H. Steps</h3>
          <div className="space-y-3">
            {wealthSteps.map((step, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </div>
                <span className="text-gray-300 font-medium">{step}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold text-white mb-4">Your Next Steps</h3>
          <ul className="space-y-3">
            {nextSteps.map((step, index) => (
              <li key={index} className="flex items-start space-x-2">
                <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                <span className="text-gray-300">{step}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const WealthMethodFullReport = () => {
  // This example shows just the discover profile. Add more as needed.
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Your Full W.E.A.L.T.H. Method Blueprint</h1>
          <p className="text-lg text-gray-300">
            This comprehensive guide reveals each step of your digital journey, tailored to your profile.
          </p>
        </div>

        <ReportSection
          title="The Opportunity Seeker"
          subtitle="You're at the perfect starting point for explosive growth"
          description="You have the drive and ambition, but you need clarity on WHAT to sell and WHO to serve. The good news? This is exactly where every successful entrepreneur started."
          keyInsight="Your biggest breakthrough will come from the DISCOVER phase - finding your profitable sweet spot where passion meets market demand."
          nextSteps={[
            "Complete the personal inventory audit to uncover hidden opportunities",
            "Learn the MVP validation process to test ideas quickly",
            "Master the art of identifying customer pain points"
          ]}
          wealthSteps={["DISCOVER", "BUILD", "CONVERT"]}
          color="from-blue-500 to-cyan-500"
          icon={<Target className="w-12 h-12 text-white" />}
        />

        {/* Add more ReportSections for each profile here */}
      </div>
    </div>
  );
};

export default WealthMethodFullReport;
